import { Navigate, RouteObject, createBrowserRouter, useLocation } from "react-router-dom";
import { ReactNode } from "react";
import App from "../layout/App";
import LoginPage from "../../features/users/LoginPage";
import OriginalAppEntryPage from "../../features/users/OriginalAppEntryPage";
import RequireAuth from "./RequireAuth";
import TestErrors from "../../features/errors/TestError";
import NotFound from "../../features/errors/NotFound";
import ServerError from "../../features/errors/ServerError";
import GroupFormPage from "../../features/group/GroupFormPage";
import AccountPage from "../../features/users/AccountPage";
import SetupHealthPage from "../../features/setup/SetupHealthPage";
import ExitIframe from "./ExitIframe";
import ProductsPage from "../../features/products/ProductsPage";
import ProductsRealtimePage from "../../features/products/ProductsRealtimePage";
import VehiclesPage from "../../features/vehicles/VehiclePage";
import VehicleFormPage from "../../features/vehicles/VehicleFormPage";
import ImportsPage from "../../features/imports/ImportsPage";
import ImportHistoryPage from "../../features/imports/ImportHistoryPage";
import AtdPage from "../../features/atd/AtdPage";
import AtdOrderDetailsPage from "../../features/atd/AtdOrderDetailsPage";
import AtdOrderLinkRedirectPage from "../../features/atd/AtdOrderLinkRedirectPage";
import AtdProfilePage from "../../features/atd/AtdProfilePage";
import AtdPreviewPage from "../../features/atd/AtdPreviewPage";
import ProductGuideListPage from "../../features/productGuides/ProductGuideListPage";
import ProductGuideEditorPage from "../../features/productGuides/ProductGuideEditorPage";
import ProductDetailsPage from "../../features/products/ProductDetailsPage";
import CollectionDetailsPage from "../../features/products/CollectionDetailsPage";
import AtdProductDetailsPage from "../../features/products/AtdProductDetailsPage";
import ProductAdminLinkRedirectPage from "../../features/products/ProductAdminLinkRedirectPage";
import AdminPage from "../../features/admin/AdminPage";
import AdminStoresPage from "../../features/admin/AdminStoresPage";
import AdminLegacyStorePage from "../../features/admin/AdminLegacyStorePage";
import AdminStoreDetailsPage from "../../features/admin/AdminStoreDetailsPage";
import AdminSyncLogsPage from "../../features/admin/AdminSyncLogsPage";
import AdminSyncLogErrorsPage from "../../features/admin/AdminSyncLogErrorsPage";
import AdminErrorLogsPage from "../../features/admin/AdminErrorLogsPage";
import AdminWebhookLogsPage from "../../features/admin/AdminWebhookLogsPage";
import AdminPrivacyRequestsPage from "../../features/admin/AdminPrivacyRequestsPage";
import AdminDataFeedPage from "../../features/admin/AdminDataFeedPage";
import AdminDataFeedStoreDetailsPage from "../../features/admin/AdminDataFeedStoreDetailsPage";
import AdminFitmentImportLogsPage from "../../features/admin/AdminFitmentImportLogsPage";
import AdminSystemPage from "../../features/admin/AdminSystemPage";
import AdminTurn14Page from "../../features/admin/AdminTurn14Page";
import PreviewPage from "../../features/preview/PreviewPage";
import AdminSyncHealthPage from "../../features/admin/AdminSyncHealthPage";
import AdminActivityPage from "../../features/admin/AdminActivityPage";
import ReportsPage from "../../features/reports/ReportsPage";
import PlanPage from "../../features/plan/PlanPage";
import AdvancedSettingsPage from "../../features/settings/AdvancedSettingsPage";
import DataManagementSettingsPage from "../../features/settings/DataManagementSettingsPage";
import IntegrationsSettingsPage from "../../features/settings/IntegrationsSettingsPage";
import SelectorFieldsSettingsPage from "../../features/settings/SelectorFieldsSettingsPage";
import StorefrontTextSettingsPage from "../../features/settings/StorefrontTextSettingsPage";
import { SETTINGS_PATHS, SettingsSectionRedirect } from "../../features/settings/settingsPaths";
import AppearanceSettingsPage from "../../features/storefront/AppearanceSettingsPage";
import SearchResultsSettingsPage from "../../features/storefront/SearchResultsSettingsPage";
import ServiceBookingCalendarPage from "../../features/serviceBooking/ServiceBookingCalendarPage";
import ServiceBookingSettingsPage from "../../features/serviceBooking/ServiceBookingSettingsPage";
import { useStore } from "../stores/store";
import { isServiceBookingEnabledForShop } from "../utils/serviceBookingFeature";
import LegacySettingsPage from "../../features/legacy/LegacySettingsPage";
import LegacyPilotHandoffPage from "../../features/legacy/LegacyPilotHandoffPage";
import LegacyVehiclesPage from "../../features/legacy/LegacyVehiclesPage";
import LegacyProductMappingsPage from "../../features/legacy/LegacyProductMappingsPage";
import LegacyRouteGuard, { NativeSettingsRoute } from "../../features/legacy/LegacyRouteGuard";
import Dashboard from "../../features/home/Dashboard";
import {
    LegacyAccountPage,
    LegacyDataExportPage,
    LegacyDocumentationPage,
    LegacyTurn14Page,
    LegacyUniversalPage,
    LegacyUpgradeReviewPage,
} from "../../features/legacy/LegacySupportPages";

// App settings opens its first tab, and links to a section of the retired
// settings editor open that tab.
const SettingsHome = () => {
    const location = useLocation();
    if (new URLSearchParams(location.search).has('section')) return <SettingsSectionRedirect />;
    return <Navigate replace to={{ pathname: SETTINGS_PATHS.searchResults, hash: location.hash }} />;
};

const ServiceBookingRouteGuard = ({ children }: { children: ReactNode }) => {
    const { userStore } = useStore();
    return isServiceBookingEnabledForShop(userStore.user?.shopDomain)
        ? <>{children}</>
        : <Navigate replace to="/not-found" />;
};

export const routes: RouteObject[] = [
    { path: '/original-app', element: <OriginalAppEntryPage /> },
    // Standalone: do not run App's Shopify/OAuth initialization before redeeming.
    { path: '/legacy-pilot/handoff', element: <LegacyPilotHandoffPage /> },
    { path: '/operator-access/handoff', element: <LegacyPilotHandoffPage operator /> },
    {
        path: '/',
        element: <App />,
        children: [
            {element: <RequireAuth />, children: [  
                {path: '/', element: <Dashboard/> },
                { path: 'dashboard', element: <Dashboard /> },
                { path: 'setup-health', element: <SetupHealthPage /> },
                { path: 'products', element: <ProductsRealtimePage /> },
                { path: 'products-legacy', element: <ProductsPage /> },
                // Shopify Admin action redirect. Keep before products/:id so
                // "link" is not treated as a product id.
                { path: 'products/link', element: <ProductAdminLinkRedirectPage /> },
                { path: 'products/atd/:id', element: <AtdProductDetailsPage /> },
                { path: 'products/collections/:id', element: <CollectionDetailsPage /> },
                { path: 'products/:id', element: <ProductDetailsPage /> },
                { path: 'vehicles', element: <VehiclesPage /> },
                // Product guide builder is route-level gated in the page and hidden in the nav for other shops.
                { path: 'product-guides', element: <ProductGuideListPage /> },
                { path: 'product-guides/new', element: <ProductGuideEditorPage key='new' /> },
                { path: 'product-guides/:id', element: <ProductGuideEditorPage key='edit' /> },
                {
                    path: 'service-booking',
                    element: <ServiceBookingRouteGuard><ServiceBookingCalendarPage /></ServiceBookingRouteGuard>,
                },
                {
                    path: 'service-booking/settings',
                    element: <ServiceBookingRouteGuard><ServiceBookingSettingsPage /></ServiceBookingRouteGuard>,
                },
                { path: 'storefront', element: <SettingsSectionRedirect /> },
                { path: 'createvehicle', element: <VehicleFormPage /> },
                {path: 'groups', element: <GroupFormPage />},
                {path: 'groups/:id', element: <GroupFormPage key='manage' />},
                {path: 'creategroup', element: <GroupFormPage key='create' />},
                {path: 'settings', element: <SettingsHome />},
                {path: 'settings/search-results', element: <SearchResultsSettingsPage />},
                {path: 'settings/selector-fields', element: <NativeSettingsRoute><SelectorFieldsSettingsPage /></NativeSettingsRoute>},
                {path: 'settings/storefront-text', element: <NativeSettingsRoute><StorefrontTextSettingsPage /></NativeSettingsRoute>},
                {path: 'settings/appearance', element: <NativeSettingsRoute><AppearanceSettingsPage /></NativeSettingsRoute>},
                {path: 'settings/data', element: <DataManagementSettingsPage />},
                {path: 'settings/integrations', element: <NativeSettingsRoute><IntegrationsSettingsPage /></NativeSettingsRoute>},
                {path: 'settings/advanced', element: <AdvancedSettingsPage />},
                {path: 'imports', element: <ImportsPage />},
                {path: 'imports/history', element: <ImportHistoryPage />},
                {path: 'reports', element: <ReportsPage />},
                {path: 'plan', element: <PlanPage />},
                {path: 'integrations', element: <Navigate replace to='/settings/integrations' />},
                {path: 'integrations/atd', element: <Navigate replace to='/integrations/atd/orders' />},
                {path: 'integrations/atd/orders', element: <AtdPage />},
                {path: 'integrations/atd/orders/:id', element: <AtdOrderDetailsPage />},
                {path: 'integrations/atd/profile', element: <AtdProfilePage />},
                {path: 'integrations/atd/preview', element: <AtdPreviewPage />},
                {path: 'atd', element: <Navigate replace to='/integrations/atd/orders' />},
                // Shopify order action redirect. The page resolves Shopify's
                // order id/GID to our internal ATD order id before navigating.
                {path: 'atd/order-link', element: <AtdOrderLinkRedirectPage />},
                {path: 'atd/orders/:id', element: <AtdOrderDetailsPage />},
                {path: 'account', element: <AccountPage />},
                {path: 'legacy/settings', element: <LegacyRouteGuard><LegacySettingsPage /></LegacyRouteGuard>},
                {path: 'legacy/vehicles', element: <LegacyRouteGuard><LegacyVehiclesPage /></LegacyRouteGuard>},
                {path: 'legacy/mappings', element: <LegacyRouteGuard><LegacyProductMappingsPage /></LegacyRouteGuard>},
                {path: 'legacy/export', element: <LegacyRouteGuard><LegacyDataExportPage /></LegacyRouteGuard>},
                {path: 'legacy/universal', element: <LegacyRouteGuard><LegacyUniversalPage /></LegacyRouteGuard>},
                {path: 'legacy/documentation', element: <LegacyRouteGuard><LegacyDocumentationPage /></LegacyRouteGuard>},
                {path: 'legacy/account', element: <LegacyRouteGuard><LegacyAccountPage /></LegacyRouteGuard>},
                {path: 'legacy/turn14', element: <LegacyRouteGuard><LegacyTurn14Page /></LegacyRouteGuard>},
                {path: 'legacy/upgrade', element: <LegacyRouteGuard><LegacyUpgradeReviewPage /></LegacyRouteGuard>},
                {path: 'errors', element: <TestErrors />}
            ]},
            // {path: 'not-found', element: <NotFound />},
            {path: 'login', element: <LoginPage />},
            {path: 'not-found', element: <NotFound />},
            {path: 'server-error', element: <ServerError />},
            {path: '*', element: <Navigate replace to='/not-found' />},
        ]
    },
    {
        path: '/exit-iframe',
        element: <ExitIframe />
    },
    {
        path: '/pf-console',
        element: <AdminPage />,
        children: [
            { path: 'dashboard', element: <AdminSyncHealthPage /> },
            { path: 'activity', element: <AdminActivityPage /> },
            { path: 'stores', element: <AdminStoresPage /> },
            { path: 'legacy-stores/:sourceShopId', element: <AdminLegacyStorePage /> },
            { path: 'stores/:shopId', element: <AdminStoreDetailsPage /> },
            { path: 'sync-logs', element: <AdminSyncLogsPage /> },
            { path: 'sync-logs/:syncLogId/errors', element: <AdminSyncLogErrorsPage /> },
            { path: 'error-logs', element: <AdminErrorLogsPage /> },
            { path: 'webhook-logs', element: <AdminWebhookLogsPage /> },
            { path: 'privacy-requests', element: <AdminPrivacyRequestsPage /> },
            { path: 'data-feed', element: <AdminDataFeedPage /> },
            { path: 'data-feed/stores/:shopId', element: <AdminDataFeedStoreDetailsPage /> },
            { path: 'fitment-import-logs', element: <AdminFitmentImportLogsPage /> },
            { path: 'turn14', element: <AdminTurn14Page /> },
            { path: 'system', element: <AdminSystemPage /> }
        ]
    },
    {
        path: '/pf-preview',
        element: <PreviewPage />
    }
]

export const router = createBrowserRouter(routes);
